import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaBars, FaTimes, FaUser } from 'react-icons/fa';
import { supabase } from '../lib/supabase';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreditsPrompt, setShowCreditsPrompt] = useState(false);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    async function checkAdminStatus() {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      setIsAdmin(data?.role === 'admin');
    }

    checkAdminStatus();
  }, [user]);

  // 控制积分提示的显示和隐藏
  useEffect(() => {
    if (user) return; // 如果用户已登录，不显示提示

    // 设置延迟显示
    const showTimer = setTimeout(() => {
      setShowCreditsPrompt(true);
      
      // 3秒后隐藏
      const hideTimer = setTimeout(() => {
        setShowCreditsPrompt(false);
      }, 3000);

      return () => clearTimeout(hideTimer);
    }, 500); // 延迟500ms显示

    return () => clearTimeout(showTimer);
  }, [user]);

  const handleSignOut = async () => {
    try {
      setIsProfileMenuOpen(false);
      setIsMenuOpen(false);
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/80 backdrop-blur-md' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/images/logo.png" alt="Parlando" className="h-16" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/home" className="text-white hover:text-purple-400 transition-colors">
              Home
            </Link>
            {/* 
            <Link to="/features" className="text-white hover:text-purple-400 transition-colors">
              Features
            </Link>
            */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-2 text-white hover:text-purple-400 transition-colors"
                >
                  <FaUser />
                  <span>{profile?.username || user.email}</span>
                </button>
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-black/90 backdrop-blur-md ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      <Link
                        to="/dashboard"
                        className="block px-4 py-2 text-sm text-white hover:bg-purple-900/50"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin/dashboard"
                          className="block px-4 py-2 text-sm text-white hover:bg-purple-900/50"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          Admin Panel
                        </Link>
                      )}
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-white hover:bg-purple-900/50"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Settings
                      </Link>
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-purple-900/50"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                >
                  Login
                </Link>
                {/* 添加免费积分提示 */}
                <div className={`absolute top-full right-0 mt-2 transition-all duration-500 transform ${
                  showCreditsPrompt ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                }`}>
                  <Link to="/login" className="block">
                    <div className="relative group cursor-pointer">
                      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/30 to-blue-600/30 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative px-4 py-2 bg-black/60 backdrop-blur-md rounded-lg border border-white/10 group-hover:border-purple-500/50 shadow-lg transition-all duration-300">
                        <p className="text-sm text-white/80 group-hover:text-white whitespace-nowrap">
                          Get 10 free credits—join now and create your first poetic masterpiece.
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white hover:text-purple-400 transition-colors"
          >
            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4">
            <Link
              to="/"
              className="block py-2 text-white hover:text-purple-400 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            {/* 
            <Link
              to="/features"
              className="block py-2 text-white hover:text-purple-400 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </Link>
            */}
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block py-2 text-white hover:text-purple-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin/dashboard"
                    className="block py-2 text-white hover:text-purple-400 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                )}
                <Link
                  to="/settings"
                  className="block py-2 text-white hover:text-purple-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Settings
                </Link>
                <button
                  onClick={handleSignOut}
                  className="block w-full text-left py-2 text-white hover:text-purple-400 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block py-2 text-white hover:text-purple-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 